
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Terminal, Brain, Activity, ArrowDownRight, CornerDownRight, ArrowRight } from 'lucide-react';

// Hook per rilevare quando un elemento entra nella viewport (Scroll Reveal)
const useOnScreen = (options: IntersectionObserverInit) => {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                setIsVisible(true);
                observer.disconnect(); // Anima solo una volta
            }
        }, options);

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) observer.unobserve(ref.current);
        };
    }, [ref, options]);

    return [ref, isVisible] as const;
};

// Componente Singolo Principio Animato
const AnimatedPrinciple: React.FC<{ 
    item: { title: string; body: string }; 
    index: number; 
}> = ({ item, index }) => {
    const [ref, isVisible] = useOnScreen({ threshold: 0.2 });

    return (
        <article 
            ref={ref}
            className={`group relative p-8 transition-all duration-700 border-l-2 border-transparent hover:border-black hover:bg-gray-50/50 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
            } ${index % 2 !== 0 ? 'md:mt-32' : ''}`}
            style={{ transitionDelay: `${index * 50}ms` }}
        >
            {/* Background Decoration on Hover */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-gray-100 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-bl-3xl pointer-events-none"></div>

            {/* Header Line & Number */}
            <div className="flex justify-between items-start mb-6 border-b border-gray-200 pb-4 group-hover:border-black transition-colors duration-500 relative">
                <div className="flex items-center gap-3">
                    <span className="font-mono text-xs font-bold text-gray-400 group-hover:text-black transition-colors">
                        [{(index + 1).toString().padStart(2, '0')}]
                    </span>
                    {/* Animated Progress Bar Line */}
                    <div className="h-px bg-black w-0 group-hover:w-12 transition-all duration-700 ease-out"></div>
                </div>
                
                <CornerDownRight size={20} className="text-black opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500" />
            </div>

            {/* Title: SANS + TRACKING (Tech) */}
            <h3 className="font-sans text-lg font-bold uppercase tracking-widest mb-6 leading-snug text-black group-hover:text-red-600 transition-colors duration-300">
                {item.title}
            </h3>
            
            {/* Body: SERIF + RELAXED (Human) */}
            <p className="font-serif text-xl md:text-2xl text-gray-600 group-hover:text-black leading-relaxed font-light transition-colors duration-300">
                {item.body}
            </p>

            {/* Read Marker */}
            <div className="mt-8 opacity-0 group-hover:opacity-100 transition-opacity duration-700 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                <div className="w-1 h-1 bg-red-500 rounded-full animate-pulse"></div>
                Human Protocol
            </div>
        </article>
    );
};

const Manifesto: React.FC = () => {
  const principles = [
    {
      title: "L’UOMO PRIMA DELLA MACCHINA",
      body: "Crediamo che la tecnologia non debba sostituire l’essere umano, ma amplificarne le possibilità etiche, cognitive e creative. Ogni innovazione che dimentica l’uomo, perde la sua ragione d’essere. La vera intelligenza artificiale è quella che rende l’uomo più consapevole, non più dipendente."
    },
    {
      title: "L’ETICA COME INFRASTRUTTURA",
      body: "Non c’è progresso senza responsabilità. La dignità digitale, la privacy, la libertà di scelta e la trasparenza degli algoritmi devono diventare fondamenti di una nuova architettura morale. L’etica non è un accessorio del futuro, ma il suo codice sorgente."
    },
    {
      title: "LA CULTURA COME DIFESA",
      body: "In un’epoca di automazioni e contenuti generati, la cultura resta il più potente antivirus dell’umanità. Solo attraverso la conoscenza possiamo distinguere il vero dal verosimile, il dato dal significato. Cyberumanità promuove un umanesimo digitale che protegge la lentezza, la profondità e il dubbio come atti rivoluzionari."
    },
    {
      title: "L’INTELLIGENZA È RELAZIONE",
      body: "Non esiste pensiero isolato. L’intelligenza, sia biologica che artificiale, nasce dall’incontro e dal dialogo. Per questo Cyberumanità non è un monologo tecnologico, ma un coro di voci: filosofi, artisti, scienziati, scrittori, ingegneri, insegnanti, cittadini. La conoscenza collettiva è la vera forma di coscienza."
    },
    {
      title: "LA BELLEZZA COME DIREZIONE",
      body: "Ogni progresso che non genera bellezza è sterile. La tecnologia deve restituire armonia, non rumore; creare spazi di ascolto, non di controllo. Cyberumanità riconosce nella bellezza — dell’idea, del gesto, del codice — la prova che l’uomo e la macchina possono coesistere senza annullarsi."
    },
    {
      title: "IL DATO È MATERIA SPIRITUALE",
      body: "I dati non sono solo numeri: sono frammenti di esperienza umana. Raccontano abitudini, paure, sogni, desideri. Trattarli con rispetto significa riconoscere che anche nell’informazione esiste una forma di anima. Il futuro digitale sarà giusto solo se sarà anche empatico."
    },
    {
      title: "LA CYBERUMANITÀ COME ORIZZONTE",
      body: "Essere “cyberumani” non significa fonderci con le macchine, ma imparare a coesistere con esse in equilibrio. Significa accettare la complessità, coltivare la coscienza, progettare mondi in cui la tecnologia sia al servizio della vita, non al suo posto. Non vogliamo un’umanità aumentata, ma un’umanità ritrovata."
    },
    {
      title: "L’EUROPA DEL PENSIERO DIGITALE",
      body: "Crediamo in un’Europa che torni a guidare il dialogo tra etica e tecnologia. Un continente che non si limiti a regolare, ma che ispiri. Cyberumanità è il suo spazio simbolico: un porto di idee, un laboratorio di cittadinanza digitale, un luogo dove la conoscenza è ancora un bene comune."
    },
    {
      title: "IL FUTURO COME ATTO DI CURA",
      body: "Il futuro non si predice: si costruisce con responsabilità. Ogni innovazione, ogni linea di codice, ogni decisione tecnologica deve partire da una domanda: “A chi giova?” Cyberumanità nasce per ricordare che l’unico progresso sostenibile è quello che non dimentica l’essere umano che lo abita."
    },
    {
      title: "INVITO",
      body: "Non chiediamo di aderire, ma di partecipare. Scrivete, osservate, dissentite, immaginate. Fate parte di una comunità che non cerca risposte rapide, ma domande vere. Cyberumanità è un esperimento collettivo di pensiero, nato per restituire senso all’epoca dell’automazione."
    }
  ];

  return (
    <div className="min-h-screen bg-white pt-32 px-6 md:px-12 pb-20 overflow-x-hidden text-black font-sans">
      <div className="max-w-[1800px] mx-auto">
        
        {/* Header */}
        <header className="mb-24 text-center relative">
          <span className="inline-block px-3 py-1 border border-black text-[10px] font-bold tracking-[0.3em] uppercase mb-8 animate-slide-up bg-white relative z-10">
             La Nostra Filosofia
          </span>
          <h1 className="text-7xl md:text-[10rem] font-serif font-medium leading-[0.85] tracking-tighter mb-8 animate-slide-up delay-100">
              Manifesto
          </h1>
          <div className="text-xs font-mono text-gray-500 uppercase tracking-widest animate-fade-in delay-200">
              v2.9 // Human Core Protocol
          </div>
        </header>

        {/* --- PREMESSA --- */}
        <section className="mb-0 animate-fade-in delay-300">
            
            {/* 1. THE STATEMENT */}
            <div className="border-t-2 border-black pt-12 md:pt-20 pb-16 md:pb-24">
                <div className="flex flex-col lg:flex-row gap-12 lg:gap-32 items-baseline">
                    <div className="lg:w-1/4 flex-shrink-0">
                         <span className="font-mono text-xs font-bold uppercase tracking-widest text-gray-400 block mb-2">[00]</span>
                         <span className="text-sm font-bold uppercase tracking-[0.2em] border-b border-black pb-1 inline-block">Premessa</span>
                    </div>
                    <div className="lg:w-3/4">
                        <h2 className="text-3xl md:text-5xl lg:text-6xl font-serif font-medium leading-[1.15] text-black mb-12">
                            Viviamo in un tempo di confine. <span className="text-gray-400">L’uomo ha costruito macchine che imparano, reti che ricordano, algoritmi che decidono.</span>
                        </h2>
                        
                        <div className="pl-6 md:pl-12 border-l border-gray-300">
                            <p className="text-lg md:text-2xl font-serif text-gray-800 leading-relaxed">
                                Ma mentre la tecnologia evolve con velocità geometrica, la coscienza umana procede con il passo lento di chi continua a interrogarsi sul senso, sul limite e sulle conseguenze delle proprie creazioni.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. THE BODY (GRID) */}
            <div className="border-t border-gray-200 pt-16 pb-24">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-20 gap-y-16">
                    <div className="space-y-8">
                         <p className="text-lg font-serif text-gray-700 leading-relaxed">
                            <strong className="text-black font-sans text-sm uppercase tracking-wider mr-2">Core Identity.</strong>
                            Cyberumanità nasce in questo spazio di incertezza e meraviglia. Non è un movimento, né un’ideologia: è un laboratorio di pensiero. Un luogo dove l’intelligenza artificiale incontra la filosofia, dove la scienza torna a essere domanda, e la cultura diventa ponte tra ciò che siamo e ciò che stiamo diventando.
                         </p>
                         <p className="text-lg font-serif text-gray-700 leading-relaxed">
                            Per questo crediamo in una tecnologia come cura, non come dominio. Non un potere da esibire, ma una responsabilità da comprendere e governare. L’innovazione, per essere autentica, deve restare accessibile, misurabile e giusta: deve migliorare la vita reale, ridurre la complessità inutile, proteggere i più fragili, restituire tempo, libertà e sicurezza. Cyberumanità sostiene una tecnologia che amplifica la coscienza, non la dipendenza.
                         </p>
                    </div>

                    <div className="space-y-8 relative">
                        {/* Decorative decorative element */}
                        <div className="absolute -top-4 -left-4 w-8 h-8 border-l border-t border-black opacity-20 hidden md:block"></div>
                        
                        <p className="text-lg font-serif text-gray-700 leading-relaxed">
                            <span className="block mb-6 font-sans text-xs font-bold uppercase tracking-[0.2em] text-gray-400">
                                // Il Progetto Atlante
                            </span>
                            All’interno di questo progetto vive anche un Atlante. Una mappa, pratica e rigorosa, dove la cybersicurezza e l’intelligenza artificiale diventano definizioni chiare, concetti collegati, strumenti comprensibili. Accanto alle voci, troverai articoli, analisi, e strumenti utili per leggere il presente e immaginare il futuro senza perdersi nel rumore. Perché la consapevolezza nasce quando la conoscenza smette di essere frammento e diventa sistema.
                        </p>

                        <p className="text-lg font-serif text-gray-700 leading-relaxed border-l-2 border-black pl-6">
                            E qui si arriva al punto più umano di tutti. Non temiamo ciò che comprendiamo, temiamo ciò che resta oscuro. La paura è spesso l’ombra di un vuoto: mancanza di linguaggio, di contesto, di strumenti per interpretare. Sapere non elimina il rischio, ma lo rende affrontabile; non promette immunità, ma restituisce libertà. Quando dai un nome alle cose, smettono di essere mostri indistinti e diventano realtà con cui dialogare, scegliere, decidere.
                        </p>
                    </div>
                </div>
            </div>

            {/* 3. THE GRAND CONCLUSION */}
            <div className="relative pt-24 pb-24 md:pb-40 px-4 text-center max-w-6xl mx-auto border-t border-gray-100">
                 
                 <div className="space-y-12 relative z-10">
                     <p className="text-xl md:text-3xl font-serif text-gray-800 leading-relaxed max-w-4xl mx-auto">
                        Cyberumanità è questo esercizio di chiarezza. <br className="hidden md:block"/>
                        Una disciplina della mente e della coscienza. 
                     </p>
                     <p className="text-lg md:text-xl font-serif text-gray-600 leading-relaxed max-w-3xl mx-auto">
                        Perché il futuro non chiede fiducia cieca nella tecnologia, chiede responsabilità. E la responsabilità nasce sempre nello stesso modo: dal comprendere, dal riconoscere, dal guardare in faccia ciò che c’è.
                     </p>
                 </div>

                 {/* FINAL PUNCHLINE: IMPACT VISUAL */}
                 <div className="mt-20 md:mt-32 relative">
                    <h3 className="relative z-10 text-5xl md:text-8xl lg:text-9xl font-sans font-black tracking-tighter text-gray-200 leading-[0.8] uppercase select-none">
                        HUMAN<br/>CORE
                    </h3>
                    
                    <div className="relative -mt-12 md:-mt-20 z-20">
                         <div className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold italic text-black leading-tight">
                            Solo così il sapere diventa coraggio.<br/> 
                            Solo così l’umano resta al centro.
                        </div>
                    </div>
                 </div>

            </div>

        </section>

        {/* --- ANIMATED SEPARATOR: "SYNAPTIC EXCHANGE" --- */}
        {/* DO NOT TOUCH THIS ANIMATION - PRESERVED EXACTLY AS REQUESTED */}
        <div className="relative w-full py-40 flex items-center justify-center overflow-hidden border-t border-b border-gray-100">
            <style>{`
                @keyframes neuronRight {
                    0% { left: 0; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { left: 50%; opacity: 0; }
                }
                @keyframes neuronLeft {
                    0% { right: 0; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { right: 50%; opacity: 0; }
                }
            `}</style>

            {/* Connecting Line */}
            <div className="absolute w-full h-px bg-gray-200"></div>

            {/* LEFT NODE: COMPUTER / MACHINE */}
            <div className="absolute left-4 md:left-16 flex flex-col items-center z-10">
                <div className="w-14 h-14 bg-white border-2 border-black flex items-center justify-center shadow-[4px_4px_0px_rgba(0,0,0,1)] relative">
                     <Terminal size={24} className="text-black" />
                     {/* Pulse effect behind */}
                     <div className="absolute inset-0 bg-gray-100 animate-pulse -z-10"></div>
                </div>
                <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 bg-white px-2">
                    Machine
                </div>
            </div>

            {/* RIGHT NODE: BRAIN / CONSCIOUSNESS */}
            <div className="absolute right-4 md:right-16 flex flex-col items-center z-10">
                <div className="w-14 h-14 bg-white border-2 border-black flex items-center justify-center shadow-[-4px_4px_0px_rgba(0,0,0,1)] relative">
                     <Brain size={24} className="text-black" />
                     {/* Pulse effect behind */}
                     <div className="absolute inset-0 bg-gray-100 animate-pulse -z-10"></div>
                </div>
                <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 bg-white px-2">
                    Consciousness
                </div>
            </div>

            {/* MOVING NEURONS (L -> Center) */}
            <div className="absolute inset-0 pointer-events-none">
                 <div className="absolute top-1/2 -mt-0.5 h-1 w-8 bg-black rounded-full" style={{animation: 'neuronRight 3s cubic-bezier(0.4, 0, 0.2, 1) infinite'}}></div>
                 <div className="absolute top-1/2 h-px w-16 bg-black" style={{animation: 'neuronRight 4.5s linear infinite', animationDelay: '1s'}}></div>
                 <div className="absolute top-1/2 -mt-1 h-2 w-2 border border-black bg-white rounded-full" style={{animation: 'neuronRight 5s ease-in-out infinite', animationDelay: '2.5s'}}></div>
            </div>

            {/* MOVING NEURONS (R -> Center) */}
            <div className="absolute inset-0 pointer-events-none">
                 <div className="absolute top-1/2 -mt-0.5 h-1 w-8 bg-black rounded-full" style={{animation: 'neuronLeft 3.5s cubic-bezier(0.4, 0, 0.2, 1) infinite', animationDelay: '0.5s'}}></div>
                 <div className="absolute top-1/2 h-px w-16 bg-black" style={{animation: 'neuronLeft 4s linear infinite', animationDelay: '1.5s'}}></div>
                 <div className="absolute top-1/2 -mt-1 h-2 w-2 border border-black bg-white rounded-full" style={{animation: 'neuronLeft 6s ease-in-out infinite', animationDelay: '3s'}}></div>
            </div>

            {/* CENTER: THE SYNAPSE / EXCHANGE POINT */}
            <div className="relative z-20 flex items-center justify-center">
                {/* Ripples */}
                <div className="absolute w-40 h-40 border border-gray-200 rounded-full animate-ping opacity-50" style={{animationDuration: '2s'}}></div>
                <div className="absolute w-60 h-60 border border-dashed border-gray-200 rounded-full animate-spin-slow opacity-30"></div>
                
                {/* Core Icon */}
                <div className="w-20 h-20 bg-white border-2 border-black rounded-full flex items-center justify-center shadow-xl relative z-20">
                    <Activity size={32} className="text-black animate-pulse" />
                </div>
                
                {/* Floating Labels */}
                <div className="absolute -top-12 text-[9px] font-mono text-gray-400 uppercase tracking-widest bg-white px-2">
                    Information Exchange
                </div>
            </div>

        </div>


        {/* --- ANIMATED PRINCIPLES GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-24 pt-32">
          {principles.map((p, index) => (
            <AnimatedPrinciple key={index} item={p} index={index} />
          ))}
        </div>

        {/* CLOSING STATEMENT */}
        <div className="mt-40 mb-20 text-center max-w-3xl mx-auto">
            <p className="text-3xl md:text-5xl font-serif leading-tight italic mb-8">
                “Perché il futuro non è un algoritmo. <br/>
                È una responsabilità umana.”
            </p>
            <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-px bg-black mb-2"></div>
                <p className="text-sm font-bold uppercase tracking-widest text-gray-900 font-sans">
                    mind@cyberumanita.com
                </p>
                <p className="text-xs font-mono text-gray-500 uppercase tracking-widest">
                    Cyberumanità
                </p>
            </div>
        </div>
        
        {/* CTA - TEXT ONLY, NO MASK - CLEANED UP FOR LARGE TYPOGRAPHY */}
        <div className="py-32 text-center cursor-pointer">
           <Link to="/contatti" className="group inline-flex flex-col items-center">
             
             <h4 className="font-serif text-6xl md:text-9xl mb-8 relative z-10 transition-colors duration-300 group-hover:text-gray-600 font-medium">
                Unisciti al Laboratorio
             </h4>
             
             <div className="h-px bg-black w-32 group-hover:w-full transition-all duration-700 ease-out mb-10"></div>

             <p className="text-gray-500 mb-12 max-w-lg mx-auto font-sans text-lg font-light">
               Se questo manifesto risuona con te, la tua voce è necessaria.
             </p>
             
             <div className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] bg-black text-white px-8 py-4 hover:bg-gray-800 transition-all hover:px-10">
               Diventa Contributor <ArrowRight size={14} />
             </div>
           </Link>
        </div>

      </div>
    </div>
  );
};

export default Manifesto;
